import React from 'react';
import { Box, Typography, Paper, Chip, CircularProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  timelineItemClasses,
} from '@mui/lab';
import CommentIcon from '@mui/icons-material/Comment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HistoryIcon from '@mui/icons-material/History';
import { formatDistanceToNow } from 'date-fns';
import useCaseActivityLog from 'containers/pages/cases/hooks/useCaseActivityLog';

interface CaseActivityLogProps {
  caseId: number;
  createdOn?: string;
  updatedOn?: string;
}

function parseDateStr(dateStr: string): Date {
  const dotNetMatch = /\/Date\((-?\d+)\)\//.exec(dateStr);
  return dotNetMatch
    ? new Date(parseInt(dotNetMatch[1], 10))
    : new Date(dateStr);
}

function safeTimeAgo(dateStr: string): string {
  try {
    const d = parseDateStr(dateStr);
    if (isNaN(d.getTime())) return '';
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return '';
  }
}

function formatDate(dateStr: string): string {
  try {
    const d = parseDateStr(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function formatHours(regular: number, over: number): string {
  const parts: string[] = [];
  if (regular > 0) parts.push(`${regular}h`);
  if (over > 0) parts.push(`${over}h OT`);
  return parts.join(' + ') || '0h';
}

function CaseActivityLog({ caseId, createdOn, updatedOn }: CaseActivityLogProps) {
  const { activities, loading } = useCaseActivityLog(caseId);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (activities.length === 0 && !createdOn) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">No activity recorded</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Activity Log ({activities.length} entries)
      </Typography>
      <Timeline
        sx={{
          p: 0,
          m: 0,
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
      >
        {activities.map((entry, index) => {
          const isLast = index === activities.length - 1 && !createdOn;
          return (
            <TimelineItem key={entry.id}>
              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    bgcolor: entry.billable ? 'success.main' : 'primary.main',
                    m: 0,
                    p: 0.5,
                  }}
                >
                  <CommentIcon fontSize="small" />
                </TimelineDot>
                {!isLast && (
                  <TimelineConnector sx={{ bgcolor: 'divider' }} />
                )}
              </TimelineSeparator>
              <TimelineContent sx={{ py: 1, px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight="medium">
                    {entry.activityTitle || entry.taskTitle || 'Activity'}
                  </Typography>
                  {entry.billable && (
                    <Chip label="Billable" size="small" color="success" variant="outlined" />
                  )}
                  <Chip
                    icon={<AccessTimeIcon />}
                    label={formatHours(entry.timeRegular, entry.timeOver)}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                {entry.description && (
                  <Box
                    sx={{
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      color: 'text.secondary',
                      typography: 'body2',
                      '& p': { m: 0 },
                      '& img': { maxWidth: '100%', height: 'auto' },
                    }}
                    dangerouslySetInnerHTML={{ __html: entry.description }}
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  {formatDate(entry.date)} ({safeTimeAgo(entry.date)})
                </Typography>
              </TimelineContent>
            </TimelineItem>
          );
        })}
        {createdOn && (
          <TimelineItem>
            <TimelineSeparator>
              <TimelineDot
                sx={(theme) => ({
                  bgcolor: theme.palette.grey[500],
                  m: 0,
                  p: 0.5,
                })}
              >
                <HistoryIcon fontSize="small" />
              </TimelineDot>
            </TimelineSeparator>
            <TimelineContent sx={{ py: 1, px: 2 }}>
              <Typography variant="body2" fontWeight="medium">
                Case Created
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(createdOn)} ({safeTimeAgo(createdOn)})
              </Typography>
            </TimelineContent>
          </TimelineItem>
        )}
      </Timeline>
    </Paper>
  );
}

export default CaseActivityLog;
